export interface ValidationError {
    field: string;
    message: string;
}

export const validateName = (name: string): ValidationError | null => {
    if (!name.trim()) {
        return { field: 'name', message: 'Name is required' };
    }
    if (name.length < 2) {
        return { field: 'name', message: 'Name must be at least 2 characters' };
    }
    if (/\d/.test(name)) {
        return { field: 'name', message: 'Name cannot contain numbers' };
    }
    if (!/^[a-zA-Z\s\-']+$/.test(name)) {
        return { field: 'name', message: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
    }
    return null;
};

export const validatePhone = (phone: string): ValidationError | null => {
    if (!phone.trim()) {
        return { field: 'phone', message: 'Phone number is required' };
    }
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        return { field: 'phone', message: 'Phone number must be between 10 and 15 digits' };
    }
    return null;
};

export const validateAddress = (address: string): ValidationError | null => {
    if (!address.trim()) {
        return { field: 'address', message: 'Address is required' };
    }
    if (address.length < 5) {
        return { field: 'address', message: 'Address must be at least 5 characters' };
    }
    return null;
};

export const validateCity = (city: string): ValidationError | null => {
    if (!city.trim()) {
        return { field: 'city', message: 'City is required' };
    }
    if (city.length < 2) {
        return { field: 'city', message: 'City must be at least 2 characters' };
    }
    if (!/^[a-zA-Z\s\-']+$/.test(city)) {
        return { field: 'city', message: 'City can only contain letters, spaces, hyphens, and apostrophes' };
    }
    return null;
};

export const validateState = (state: string): ValidationError | null => {
    if (!state.trim()) {
        return { field: 'state', message: 'State is required' };
    }
    if (state.length !== 2) {
        return { field: 'state', message: 'State must be a 2-letter code' };
    }
    if (!/^[A-Z]{2}$/.test(state)) {
        return { field: 'state', message: 'State must be a valid 2-letter code' };
    }
    return null;
};

export const validateZipCode = (zipCode: string): ValidationError | null => {
    if (!zipCode.trim()) {
        return { field: 'zipCode', message: 'ZIP code is required' };
    }
    if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
        return { field: 'zipCode', message: 'ZIP code must be in format 12345 or 12345-6789' };
    }
    return null;
};

export const validateCardNumber = (cardNumber: string): ValidationError | null => {
    if (!cardNumber.trim()) {
        return { field: 'cardNumber', message: 'Card number is required' };
    }
    // Remove spaces and dashes
    const cleanNumber = cardNumber.replace(/[\s-]/g, '');
    if (!/^\d{13,19}$/.test(cleanNumber)) {
        return { field: 'cardNumber', message: 'Card number must be between 13 and 19 digits' };
    }
    // Luhn algorithm for card number validation
    let sum = 0;
    let isEven = false;
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cleanNumber.charAt(i));
        if (isEven) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
        isEven = !isEven;
    }
    if (sum % 10 !== 0) {
        return { field: 'cardNumber', message: 'Invalid card number' };
    }
    return null;
};

export const validateExpiryDate = (month: string, year: string): ValidationError | null => {
    if (!month || !year) {
        return { field: 'expiry', message: 'Expiry date is required' };
    }
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    const expMonth = parseInt(month);
    const expYear = parseInt(year.length === 2 ? '20' + year : year);
    
    if (expMonth < 1 || expMonth > 12) {
        return { field: 'expiry', message: 'Invalid month' };
    }
    
    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        return { field: 'expiry', message: 'Card has expired' };
    }
    return null;
};

export const validateCVV = (cvv: string): ValidationError | null => {
    if (!cvv.trim()) {
        return { field: 'cvv', message: 'CVV is required' };
    }
    if (!/^\d{3,4}$/.test(cvv)) {
        return { field: 'cvv', message: 'CVV must be 3 or 4 digits' };
    }
    return null;
}; 